import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { API_CONFIG } from '../constants/api.constants';
import {
  CreateIllustrationRequest,
  CreateIllustrationResponse,
  GetIllustrationsResponse,
  IllustrationItem,
  IllustrationCustomer,
  CategoryData,
  FeeData
} from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class IllustrationsService {
  private baseUrl = API_CONFIG.BASE_URL;

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Creates a new illustration with categories and customer relationships
   */
  createIllustration(data: CreateIllustrationRequest): Observable<CreateIllustrationResponse> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const token = localStorage.getItem('token');
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.http.post<CreateIllustrationResponse>(
      `${this.baseUrl}/illustrations/create`,
      data,
      { headers }
    ).pipe(
      catchError(error => {
        // Show error message to user with actual error details
        const errorMessage = error.error?.message || error.message || 'Ismeretlen hiba történt';
        this.snackBar.open(`Hiba történt az illusztráció létrehozása során: ${errorMessage}`, 'Bezárás', {
          duration: 7000,
          panelClass: ['error-snackbar']
        });
        
        return throwError(() => new Error('Failed to create illustration'));
      })
    );
  }

  /**
   * Get current user ID from token or API
   */
  getCurrentUserId(): Observable<number> {
    let headers = new HttpHeaders();
    const token = localStorage.getItem('token');
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.http.get<{id: number}>(`${this.baseUrl}/auth/me`, { headers })
      .pipe(
        map(response => response.id),
        catchError(error => {
          const errorMessage = error.error?.message || error.message || 'Ismeretlen hiba történt';
          this.snackBar.open(`Hiba történt a felhasználói adatok lekérése során: ${errorMessage}`, 'Bezárás', {
            duration: 7000,
            panelClass: ['error-snackbar']
          });
          return throwError(() => new Error('Failed to get current user'));
        })
      );
  }

  /**
   * Get all illustrations with complete data from database
   */
  getIllustrations(): Observable<GetIllustrationsResponse> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const token = localStorage.getItem('token');
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.http.get<GetIllustrationsResponse>(
      `${this.baseUrl}/illustrations/`,
      { headers }
    ).pipe(
      map(response => {
        // Process the data to format dates and other fields properly
        if (response.success && response.data) {
          response.data = response.data.map(item => ({
            ...item,
            senddate: item.senddate,
            createdat: item.createdat,
            // Calculate age if birthdate is available for customers
            customers: item.customers?.map(customer => ({
              ...customer,
              age: customer.birthdate ? this.calculateAge(new Date(customer.birthdate)) : null
            })) || null
          }));
        }
        return response;
      }),
      catchError(error => {
        const errorMessage = error.error?.message || error.message || 'Ismeretlen hiba történt';
        this.snackBar.open(`Nem sikerült betölteni az illusztrációkat: ${errorMessage}`, 'Bezárás', {
          duration: 7000,
          panelClass: ['error-snackbar']
        });
        return throwError(() => new Error('Nem sikerült betölteni az illusztrációkat'));
      })
    );
  }

  /**
   * Update illustration status
   */
  updateIllustrationStatus(illustrationId: number, newStatus: string): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const token = localStorage.getItem('token');
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    const requestBody = {
      new_status: newStatus
    };

    return this.http.patch(
      `${this.baseUrl}/illustrations/${illustrationId}/status`,
      requestBody,
      { headers }
    ).pipe(
      catchError(error => {
        const errorMessage = error.error?.message || error.message || 'Ismeretlen hiba történt';
        this.snackBar.open(`Nem sikerült frissíteni az illusztráció státuszát: ${errorMessage}`, 'Bezárás', {
          duration: 7000,
          panelClass: ['error-snackbar']
        });
        return throwError(() => new Error('Nem sikerült frissíteni az illusztráció státuszát'));
      })
    );
  }

  /**
   * Calculate age from birthdate
   */
  private calculateAge(birthDate: Date): number {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }
}